import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Typography,
  Paper,
  Grid,
  Backdrop,
  Chip,
  lighten,
  Button,
  ButtonGroup,
  Avatar,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import clsx from "clsx";
import moment from "moment";

import DeleteIcon from "@material-ui/icons/Delete";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableHead from "@material-ui/core/TableHead";

import { useDispatch, useSelector } from "react-redux";
import { TablePaginationActions } from "../../utils/TablePaginationAction";
import Search from "../../utils/Search";
import { getVehicleSignsByUser, getVehicleSigns } from "../../actions/vehicleAction";
import CreateVehicleSignModal from "../Modals/Vehicle/CreateVehicleSignModal";
import DeleteVehicleSignModal from "../Modals/Vehicle/DeleteVehicleSignModal";
import Feedback from "../../utils/Feedback";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    overflow: "hidden",
  },
  title: {
    color: lighten(theme.palette.common.black, 0.75),
    display: "block",
    fontSize: 15,
    fontWeight: 900,
  },
  paper: {
    padding: theme.spacing(3),
  },
  btn2: {
    marginBottom: 30,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  table: {
    minWidth: 350,
  },
  chip: {
    paddingLeft: 8,
    paddingRight: 8,
    fontSize: "0.8rem",
  },
  statusSignIn: {
    borderColor: "#4caf50",
    color: "#4caf50",
  },
  statusSignOut: {
    borderColor: "#ff9800",
    color: "#ff9800",
  },
  green: {
    color: "#4caf50",
    fontWeight: 500,
  },
  orange: {
    color: "#ff9800",
    fontWeight: 500,
  },
}));

const VehicleSign = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [getId, setGetId] = useState("");

  const [feed, setFeed] = React.useState({
    loading: false,
    open: false,
    message: "",
    success: false,
  });

  const { loading, error, vehicleSignsByUser, vehicleSigns } = useSelector((state) => state.vehicle);
  const { profile } = useSelector((state) => state.users);

  const isAdmin = profile?.role?.some((role) =>
    ["admin", "vehicle_admin", "vehicle_manager", "vehicle_supervisor"].includes(role.name)
  );

  useEffect(() => {
    if (isAdmin) {
      dispatch(getVehicleSigns());
    } else {
      dispatch(getVehicleSignsByUser());
    }
  }, [dispatch, isAdmin]);

  useEffect(() => {
    const abortController = new AbortController();
    if (error && error !== null) {
      setFeed((prev) => ({
        loading: false,
        open: true,
        message: error,
        success: false,
      }));
    }
    return () => {
      abortController.abort();
    };
  }, [error]);

  const updateSearch = (e) => {
    setSearch(e.target.value.substr(0, 20));
  };

  const ResetSearch = (e) => {
    e.preventDefault();
    setSearch("");
  };

  const handleOpenCreate = () => {
    setOpenCreate(true);
  };

  const handleCloseCreate = () => {
    setOpenCreate(false);
  };

  const handleOpenDelete = (id) => {
    setGetId(id);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
  };

  const records = isAdmin ? vehicleSigns : vehicleSignsByUser;

  let filteredRows = () =>
    records === undefined
      ? []
      : records.filter((row) => {
          if (search !== "") {
            return (
              row.operator?.toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
              row.vehicle?.displayname?.toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
              row.jobNumber?.toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
              row.jobSite?.toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
              row.signType?.toLowerCase().indexOf(search.toLowerCase()) !== -1
            );
          } else {
            return row;
          }
        });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 20));
    setPage(0);
  };

  const handleCloseFeed = () => {
    setFeed((prevState) => ({ ...prevState, open: false }));
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, Array(50) - page * rowsPerPage);

  return (
    <div className={classes.root}>
      <CreateVehicleSignModal {...{ openCreate, handleCloseCreate }} />
      <DeleteVehicleSignModal {...{ openDelete, handleCloseDelete, id: getId }} />

      {feed.success === false && (
        <Feedback
          handleCloseFeed={handleCloseFeed}
          open={feed.open}
          severity="error"
          message={feed.message}
        />
      )}
      
      <Typography className={classes.title} variant="overline">
        Vehicle Sign In/Out
      </Typography>
      
      <Grid container alignItems="center" spacing={3}>
        <Grid item>
          <Button
            color="primary"
            variant="contained"
            onClick={handleOpenCreate}
            className={classes.btn2}
            startIcon={<DirectionsCarIcon />}
          >
            Sign In/Out Vehicle
          </Button>
        </Grid>
        <Grid item>
          <Search
            updateSearch={updateSearch}
            placeholder="Search..."
            search={search}
            ResetSearch={ResetSearch}
          />
        </Grid>
      </Grid>

      <Paper>
        <TableContainer>
          <Table className={classes.table} aria-label="custom pagination table">
            <TableHead>
              <TableRow>
                <TableCell>Operator</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Sign Type</TableCell>
                <TableCell>Date/Time</TableCell>
                <TableCell>Job Number</TableCell>
                <TableCell>Job Site</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rowsPerPage > 0
                ? filteredRows().slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                : filteredRows()
              ).map((row, i) => {
                return (
                  <TableRow hover key={row._id}>
                    <TableCell component="th" scope="row">
                      {row.operator}
                    </TableCell>

                    <TableCell component="th" scope="row">
                      <Chip
                        avatar={
                          <Avatar>
                            <DirectionsCarIcon />
                          </Avatar>
                        }
                        label={row.vehicle?.displayname || row.vehicle?.name}
                        className={classes.chip}
                      />
                    </TableCell>

                    <TableCell component="th" scope="row">
                      <Chip
                        label={row.signType}
                        variant="outlined"
                        className={
                          row.signType === "Sign In"
                            ? clsx(classes.chip, classes.statusSignIn)
                            : clsx(classes.chip, classes.statusSignOut)
                        }
                      />
                    </TableCell>

                    <TableCell component="th" scope="row">
                      <Typography variant="body2">
                        {row.signType === "Sign In"
                          ? moment(row.signedInAt).format("MMM DD, YYYY h:mm A")
                          : moment(row.signedOutAt).format("MMM DD, YYYY h:mm A")}
                      </Typography>
                    </TableCell>

                    <TableCell component="th" scope="row">
                      {row.jobNumber || "N/A"}
                    </TableCell>

                    <TableCell component="th" scope="row">
                      {row.jobSite || "N/A"}
                    </TableCell>

                    <TableCell>
                      <ButtonGroup variant="contained">
                        <Tooltip title="Delete">
                          <Button
                            color="secondary"
                            onClick={() => handleOpenDelete(row._id)}
                          >
                            <DeleteIcon />
                          </Button>
                        </Tooltip>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                );
              })}

              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={7} />
                </TableRow>
              )}
              {filteredRows().length === 0 && (
                <TableRow hover>
                  <TableCell
                    colSpan={7}
                    align="center"
                    style={{ fontWeight: 900, color: "#616161", textAlign: "center" }}
                    component="th"
                    scope="row"
                  >
                    No Sign Records To Display
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, { label: "All", value: -1 }]}
          colSpan={7}
          count={filteredRows().length}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{
            inputProps: { "aria-label": "rows per page" },
            native: true,
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
      </Paper>
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default VehicleSign;